import { ButtonHTMLAttributes, ReactNode } from 'react'
import { useAccessibility } from '../contexts/AccessibilityContext'

interface SeniorButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode
  variant?: 'primary' | 'secondary' | 'danger'
  size?: 'small' | 'medium' | 'large'
  icon?: ReactNode
}

export const SeniorButton = ({ 
  children, 
  variant = 'primary', 
  size = 'large',
  icon,
  className = '',
  disabled,
  ...props 
}: SeniorButtonProps) => {
  const { settings } = useAccessibility()

  const getVariantClasses = () => {
    if (settings.contrast === 'high') {
      switch (variant) {
        case 'primary':
          return 'bg-black text-white border-2 border-black hover:bg-gray-800 focus:bg-gray-800'
        case 'secondary':
          return 'bg-white text-black border-2 border-black hover:bg-gray-100 focus:bg-gray-100'
        case 'danger':
          return 'bg-red-700 text-white border-2 border-red-700 hover:bg-red-800 focus:bg-red-800'
      }
    }
    
    switch (variant) {
      case 'primary':
        return 'bg-blue-600 text-white border-2 border-blue-600 hover:bg-blue-700 focus:bg-blue-700'
      case 'secondary':
        return 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50 focus:bg-gray-50'
      case 'danger':
        return 'bg-red-600 text-white border-2 border-red-600 hover:bg-red-700 focus:bg-red-700'
    }
  }

  const getSizeClasses = () => {
    const baseSize = settings.fontSize === 'extra-large' ? 'text-2xl' : 
                     settings.fontSize === 'large' ? 'text-xl' : 
                     settings.fontSize === 'medium' ? 'text-lg' : 'text-base'
    
    switch (size) {
      case 'small':
        return `${baseSize} px-4 py-2 min-h-12`
      case 'medium':
        return `${baseSize} px-6 py-3 min-h-14`
      case 'large':
        return `${baseSize} px-8 py-4 min-h-16`
    }
  }

  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'

  return (
    <button
      type="button"
      className={`
        ${getVariantClasses()}
        ${getSizeClasses()}
        ${disabledClasses}
        font-semibold
        rounded-lg
        transition-colors
        focus:outline-none
        focus:ring-4
        focus:ring-blue-300
        flex
        items-center
        justify-center
        gap-3
        ${className}
      `}
      disabled={disabled}
      {...props}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span>{children}</span>
    </button>
  )
}